import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Clock, Mail, ChevronDown, ChevronUp, Send, CheckCircle2, AlertCircle,
  Eye, RotateCcw, User as UserIcon, Pencil, MessageSquare, FileText,
} from 'lucide-react';
import EmailComposeModal from '@/components/EmailComposeModal';
import SendEmailPicker from '@/components/SendEmailPicker';
import OpsModal from '@/components/ops/OpsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } });

const STAGE_TONE = {
  'Paid - Discovery Pending': 'paid',
  'New Lead':            'neutral',
  'Contacted':           'neutral',
  'Discovery Scheduled': 'info',
  'Discovery Completed': 'info',
  'Follow-Up Sent':      'info',
  'Selections Received': 'warning',
  'Interview Scheduled': 'warning',
  'Interview Completed': 'warning',
  'Sent to Production':  'success',
};
const TONE = {
  neutral: 'bg-slate-100 text-slate-600 border-slate-200',
  info:    'bg-blue-50 text-blue-700 border-blue-100',
  warning: 'bg-amber-50 text-amber-700 border-amber-100',
  success: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  paid:    'bg-emerald-100 text-emerald-800 border-emerald-200 font-medium',
  danger:  'bg-red-50 text-red-700 border-red-100',
};
const TASK_TONE = {
  complete:    TONE.success,
  in_progress: TONE.warning,
  blocked:     TONE.danger,
  not_started: TONE.neutral,
};

function StagePill({ stage }) {
  return <span className={`ops-pill ${TONE[STAGE_TONE[stage] || 'neutral']}`}>{stage}</span>;
}

export default function LeadDetailModal({ leadId, stages, users, onClose, onUpdated, isAdmin }) {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState([]);
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState('');
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showCompose, setShowCompose] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [pickedTemplate, setPickedTemplate] = useState(null);
  const [expandedEmail, setExpandedEmail] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [productionJob, setProductionJob] = useState(null);
  const [productionTasks, setProductionTasks] = useState([]);

  const fetchLead = useCallback(async () => {
    try {
      const [leadRes, notesRes, emailsRes] = await Promise.all([
        axios.get(`${API}/ops/leads/${leadId}`, getAuth()),
        axios.get(`${API}/ops/leads/${leadId}/notes`, getAuth()),
        axios.get(`${API}/ops/leads/${leadId}/emails`, getAuth()),
      ]);
      setLead(leadRes.data);
      setNotes(notesRes.data);
      setEmails(emailsRes.data);
      setEditForm(leadRes.data);
    } catch { toast.error('Failed to load lead'); onClose(); }
    finally { setLoading(false); }
  }, [leadId, onClose]);
  useEffect(() => { fetchLead(); }, [fetchLead]);

  const fetchProductionStatus = useCallback(async () => {
    if (!lead?.id || lead.current_stage !== 'Sent to Production') { setProductionJob(null); setProductionTasks([]); return; }
    try {
      const { data: jobs } = await axios.get(`${API}/ops/production-jobs?include=all`, getAuth());
      const job = jobs.find(j => j.lead_id === lead.id && j.status !== 'Recalled');
      if (job) {
        setProductionJob(job);
        const { data: t } = await axios.get(`${API}/ops/production-jobs/${job.id}/tasks`, getAuth());
        setProductionTasks(t);
      }
    } catch {}
  }, [lead?.id, lead?.current_stage]);
  useEffect(() => { fetchProductionStatus(); }, [fetchProductionStatus]);

  const changeStage = async (newStage) => {
    try {
      await axios.post(`${API}/ops/leads/${leadId}/stage`, { stage: newStage }, getAuth());
      toast.success(`Moved to ${newStage}`);
      fetchLead(); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/ops/leads/${leadId}/notes`, { text: noteText.trim() }, getAuth());
      setNoteText('');
      fetchLead();
    } catch { toast.error('Failed to add note'); }
    finally { setSending(false); }
  };

  const saveEdit = async () => {
    try {
      const payload = {};
      const fields = ['client_first_name','client_last_name','client_email','client_phone','loved_one_first_name','loved_one_pronouns','source','city','state','full_address','initial_contact_date','interview_date','interview_time','interview_location','where_in_home','who_present','status'];
      fields.forEach(k => { if (editForm[k] !== lead[k]) payload[k] = editForm[k]; });
      if (Object.keys(payload).length === 0) { setEditing(false); return; }
      await axios.put(`${API}/ops/leads/${leadId}`, payload, getAuth());
      toast.success('Lead updated');
      setEditing(false);
      fetchLead(); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const assignLead = async (ownerId) => {
    try {
      await axios.post(`${API}/ops/leads/${leadId}/assign`, { owner_id: ownerId || null }, getAuth());
      toast.success('Lead reassigned');
      fetchLead(); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const sendToProduction = async () => {
    if (!window.confirm('Send this lead to Production? This will lock the lead and create a production job. This action cannot be undone.')) return;
    try {
      await axios.post(`${API}/ops/leads/${leadId}/send-to-production`, {}, getAuth());
      toast.success('Sent to Production');
      fetchLead(); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send to production'); }
  };

  const recallFromProduction = async () => {
    if (!window.confirm('Recall this lead from production? This will return it to Interview Completed status and mark the production job as recalled. Any work production has already done will be preserved in history but the job will no longer be active. Confirm?')) return;
    try {
      await axios.post(`${API}/ops/leads/${leadId}/recall-from-production`, {}, getAuth());
      toast.success('Recalled from production');
      fetchLead(); onUpdated();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to recall'); }
  };

  if (loading) {
    return (
      <div className="ops-shell fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-[2px] flex items-center justify-center">
        <div className="w-7 h-7 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin" />
      </div>
    );
  }
  if (!lead) return null;

  const ownerUser = users?.find(u => u._id === lead.owner);
  const stageIdx = stages.indexOf(lead.current_stage);
  const isSentToProduction = lead.current_stage === 'Sent to Production' || lead.is_locked;
  const readinessChecks = [
    { label: 'Interview is completed',                       ok: lead.current_stage === 'Interview Completed' || isSentToProduction },
    { label: 'Interview date is set',                        ok: !!lead.interview_date },
    { label: 'Client has submitted keepsake selections',     ok: !!lead.selections_submitted_at },
  ];
  const isReady = readinessChecks.every(c => c.ok);

  const headerActions = (
    <>
      <button
        onClick={() => window.open(`/ops/leads/${leadId}/preview`, '_blank', 'noopener')}
        data-testid="preview-as-client-button"
        className="hidden sm:flex h-8 items-center gap-1.5 text-[12px] text-slate-700 bg-white hover:bg-slate-50 border border-slate-200 hover:border-slate-300 px-2.5 rounded-md font-medium transition-colors"
      >
        <Eye size={12} /> Preview as client
      </button>
      <button
        onClick={() => setShowPicker(true)}
        data-testid="compose-email-button"
        className="h-8 flex items-center gap-1.5 text-[12px] bg-slate-900 hover:bg-slate-800 text-white px-2.5 rounded-md font-medium shadow-sm transition-colors"
      >
        <Mail size={12} /> Send email
      </button>
    </>
  );

  return (
    <>
      <OpsModal
        title={`${lead.client_first_name} ${lead.client_last_name}`}
        subtitle={
          <span className="inline-flex items-center gap-2">
            <StagePill stage={lead.current_stage} />
            <span className="text-slate-500">·</span>
            <span className="inline-flex items-center gap-1">
              <UserIcon size={11} className="text-slate-400" />
              {ownerUser ? ownerUser.name : 'Unassigned'}
            </span>
          </span>
        }
        onClose={onClose}
        size="lg"
        headerActions={headerActions}
        testid="lead-detail-modal"
      >
        <div className="px-5 py-4 space-y-5 text-[13px] text-slate-700">

          {/* Payment summary — paid leads only */}
          {lead.payment_status === 'paid' && (
            <section className="bg-emerald-50/60 border border-emerald-200 rounded-md px-3.5 py-2.5" data-testid="lead-payment-summary">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="ops-pill bg-emerald-100 text-emerald-800 border-emerald-200 px-1.5 text-[10px] shrink-0">Paid</span>
                  <span className="text-[12px] text-slate-700 truncate">
                    {lead.package_name || 'Heirloom Package'}
                  </span>
                </div>
                <span className="text-[13px] font-medium text-slate-900 tabular-nums">
                  {lead.payment_amount != null
                    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: (lead.payment_currency || 'usd').toUpperCase(), maximumFractionDigits: 2 }).format(lead.payment_amount / 100)
                    : '—'}
                </span>
              </div>
              {lead.paid_at && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Received {new Date(lead.paid_at).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              )}
            </section>
          )}

          {/* Stage row */}
          <section>
            <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-2">Pipeline stage</p>
            <div className="flex flex-wrap gap-1.5">
              {stages.map((s, i) => {
                const isCurrent = s === lead.current_stage;
                const isPast = i <= stageIdx;
                return (
                  <button
                    key={s}
                    onClick={() => changeStage(s)}
                    data-testid={`stage-btn-${i}`}
                    className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                      isCurrent
                        ? 'bg-slate-900 text-white border-slate-900 font-medium'
                        : isPast
                          ? 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200'
                          : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50 hover:text-slate-700'
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Admin: Assignment */}
          {isAdmin && (
            <section>
              <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-2">Assigned to</p>
              <select
                value={lead.owner || ''}
                onChange={e => assignLead(e.target.value)}
                data-testid="assign-owner-select"
                className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
              >
                <option value="">Unassigned</option>
                {(users || []).filter(u => (u.roles || [u.role]).includes('interviewer') && u.active !== false).map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </section>
          )}

          {/* Production Handoff */}
          <section
            data-testid="production-handoff-card"
            className={`rounded-lg border ${
              isSentToProduction ? 'border-slate-900 bg-slate-900 text-white' :
              isReady ? 'border-slate-900 bg-white' :
              'border-slate-200 bg-slate-50'
            } p-4`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className={`text-[11px] uppercase tracking-wider font-medium mb-1 ${isSentToProduction ? 'text-white/70' : 'text-slate-500'}`}>
                  Production handoff
                </p>
                <p className={`text-[13px] ${isSentToProduction ? 'text-white' : 'text-slate-900'}`}>
                  {isSentToProduction
                    ? 'Lead sent to production. This record is now locked.'
                    : isReady
                      ? 'Ready to send to production.'
                      : 'Complete these steps to enable handoff:'}
                </p>
              </div>
              {!isSentToProduction && (
                <button
                  onClick={sendToProduction}
                  disabled={!isReady}
                  data-testid="send-to-production-button"
                  className="h-8 inline-flex items-center gap-1.5 text-[12px] font-medium bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  <Send size={12} /> Send to production
                </button>
              )}
              {isSentToProduction && isAdmin && (
                <button
                  onClick={recallFromProduction}
                  data-testid="recall-from-production-button"
                  className="h-8 inline-flex items-center gap-1.5 text-[12px] font-medium bg-white text-slate-900 hover:bg-slate-100 px-3 rounded-md transition-colors shrink-0"
                >
                  <RotateCcw size={12} /> Recall
                </button>
              )}
            </div>
            {!isSentToProduction && !isReady && (
              <ul className="mt-3 space-y-1.5">
                {readinessChecks.map((c, i) => (
                  <li key={i} data-testid={`readiness-check-${i}`} className="flex items-center gap-2 text-[12px]">
                    {c.ok
                      ? <CheckCircle2 size={13} className="text-emerald-600 shrink-0" />
                      : <AlertCircle size={13} className="text-slate-400 shrink-0" />}
                    <span className={c.ok ? 'text-slate-700' : 'text-slate-500'}>{c.label}</span>
                  </li>
                ))}
              </ul>
            )}
            {lead.selections_submitted_at && lead.selected_items?.length > 0 && (
              <p className={`text-[11px] mt-3 ${isSentToProduction ? 'text-white/70' : 'text-slate-500'}`}>
                {lead.selected_items.length} keepsake{lead.selected_items.length === 1 ? '' : 's'} selected · {new Date(lead.selections_submitted_at).toLocaleDateString()}
              </p>
            )}
          </section>

          {/* Production Status (when in production) */}
          {productionJob && (() => {
            const done = productionTasks.filter(t => t.status === 'complete').length;
            const total = productionTasks.length;
            const pct = total ? (done / total) * 100 : 0;
            const statusTone = productionJob.status === 'Complete' ? TONE.success
                           : productionJob.status === 'Recalled' ? TONE.danger
                           : TONE.warning;
            return (
              <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="production-status-card">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500">Production status</p>
                  <span className={`ops-pill ${statusTone}`}>{productionJob.status}</span>
                </div>
                <p className="text-[13px] text-slate-900">{done} of {total} tasks complete</p>
                <div className="w-full h-1.5 bg-slate-100 rounded-full mt-2 overflow-hidden">
                  <div className="h-full bg-slate-900 transition-all" style={{ width: `${pct}%` }} />
                </div>
                {productionJob.overall_deadline && (
                  <p className="text-[11px] text-slate-500 mt-2">Deadline: {productionJob.overall_deadline}</p>
                )}
                <details className="mt-3 group" data-testid="production-tasks-readonly">
                  <summary className="text-[11px] uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-900 list-none flex items-center gap-1 select-none">
                    <ChevronDown size={11} className="transition-transform group-open:rotate-180" />
                    Show all tasks ({productionTasks.length})
                  </summary>
                  <ul className="mt-2 space-y-1.5">
                    {productionTasks.map(t => (
                      <li key={t.id} className="text-[12px] flex items-center justify-between gap-2 text-slate-700">
                        <span className="truncate">{t.name}</span>
                        <span className={`ops-pill ${TASK_TONE[t.status] || TONE.neutral} shrink-0`}>
                          {t.status.replace('_', ' ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </details>
              </section>
            );
          })()}

          {/* Tabs */}
          <section>
            <div className="flex items-center gap-1 border-b border-slate-200" data-testid="lead-tabs">
              {[
                { key: 'details',  label: 'Details',  icon: FileText },
                { key: 'emails',   label: `Emails (${emails.length})`, icon: Mail },
                { key: 'activity', label: `Activity (${notes.length})`, icon: MessageSquare },
              ].map(t => {
                const I = t.icon;
                const active = activeTab === t.key;
                return (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    data-testid={`tab-${t.key}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 text-[12px] font-medium transition-colors border-b-2 -mb-px ${
                      active
                        ? 'text-slate-900 border-slate-900'
                        : 'text-slate-500 border-transparent hover:text-slate-900'
                    }`}
                  >
                    <I size={12} /> {t.label}
                  </button>
                );
              })}
            </div>

            {/* Details */}
            {activeTab === 'details' && (
              <div className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] uppercase tracking-wider font-medium text-slate-500">Lead information</p>
                  {!editing ? (
                    <button
                      onClick={() => setEditing(true)}
                      data-testid="edit-lead-button"
                      className="inline-flex items-center gap-1 h-7 text-[12px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-2.5 transition-colors"
                    >
                      <Pencil size={11} /> Edit
                    </button>
                  ) : (
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => { setEditing(false); setEditForm(lead); }}
                        className="h-7 text-[12px] text-slate-600 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-2.5 transition-colors"
                      >Cancel</button>
                      <button
                        onClick={saveEdit}
                        data-testid="save-lead-button"
                        className="h-7 text-[12px] bg-slate-900 hover:bg-slate-800 text-white rounded-md px-2.5 font-medium transition-colors shadow-sm"
                      >Save</button>
                    </div>
                  )}
                </div>
                {editing ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      ['client_first_name','First name'],['client_last_name','Last name'],['client_email','Email'],['client_phone','Phone'],
                      ['loved_one_first_name',"Loved one's name"],['loved_one_pronouns','Pronouns'],['source','Source'],['status','Status'],
                      ['city','City'],['state','State'],['full_address','Address'],['initial_contact_date','Contact date'],
                      ['interview_date','Interview date'],['interview_time','Interview time'],['interview_location','Interview location'],
                      ['where_in_home','Where in home'],['who_present','Who present'],
                    ].map(([k, label]) => (
                      <div key={k} className={k === 'full_address' ? 'sm:col-span-2' : ''}>
                        <label className="text-[11px] uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
                        <input
                          value={editForm[k] || ''}
                          onChange={e => setEditForm({...editForm, [k]: e.target.value})}
                          data-testid={`edit-${k}`}
                          className="w-full h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2 text-[13px] text-slate-900 outline-none transition-colors"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                    {[
                      ['Email', lead.client_email], ['Phone', lead.client_phone],
                      ['Loved one', lead.loved_one_first_name], ['Pronouns', lead.loved_one_pronouns],
                      ['Source', lead.source], ['Status', lead.status],
                      ['City', lead.city], ['State', lead.state],
                      ['Contact date', lead.initial_contact_date], ['Interview date', lead.interview_date],
                      ['Interview time', lead.interview_time], ['Interview location', lead.interview_location],
                      ['Where in home', lead.where_in_home], ['Who present', lead.who_present],
                    ].filter(([,v]) => v).map(([label, val]) => (
                      <div key={label}>
                        <dt className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</dt>
                        <dd className="text-[13px] text-slate-900">{val}</dd>
                      </div>
                    ))}
                    {lead.full_address && (
                      <div className="sm:col-span-2">
                        <dt className="text-[11px] uppercase tracking-wider text-slate-500 mb-0.5">Address</dt>
                        <dd className="text-[13px] text-slate-900">{lead.full_address}</dd>
                      </div>
                    )}
                  </dl>
                )}
              </div>
            )}

            {/* Emails */}
            {activeTab === 'emails' && (
              <div className="pt-4">
                {emails.length === 0 ? (
                  <div className="text-center py-10 border border-dashed border-slate-200 rounded-lg">
                    <Mail size={20} className="mx-auto text-slate-300 mb-2" />
                    <p className="text-[13px] text-slate-700 mb-1">No emails sent yet</p>
                    <p className="text-[12px] text-slate-500 mb-3">Send the post-discovery follow-up to keep the conversation moving.</p>
                    <button
                      onClick={() => setShowPicker(true)}
                      className="inline-flex items-center gap-1.5 h-8 text-[12px] bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md font-medium shadow-sm transition-colors"
                    >
                      <Mail size={12} /> Send email
                    </button>
                  </div>
                ) : (
                  <ul className="space-y-2">
                    {emails.map(em => {
                      const ds = em.date_status;
                      const dsLabel = ds === 1 ? 'Date confirmed' : ds === 2 ? 'Pencilled in' : ds === 3 ? 'No date yet' : null;
                      const dsTitle = ds === 1 ? 'Interview date was confirmed at time of send' : ds === 2 ? 'Interview date was pencilled in, awaiting consent' : ds === 3 ? 'No interview date was set yet — discussing with loved one' : '';
                      const dsTone  = ds === 1 ? TONE.success : ds === 2 ? TONE.warning : ds === 3 ? TONE.neutral : '';
                      return (
                        <li key={em.id} data-testid={`email-log-${em.id}`} className="border border-slate-200 rounded-lg overflow-hidden bg-white">
                          <button
                            onClick={() => setExpandedEmail(expandedEmail === em.id ? null : em.id)}
                            className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-slate-50 transition-colors"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-[13px] font-medium text-slate-900 truncate">{em.subject}</p>
                              <div className="flex items-center gap-1.5 flex-wrap mt-1">
                                <p className="text-[11px] text-slate-500">
                                  To {em.recipient} · {em.sent_by_name} · {new Date(em.sent_at).toLocaleString()}
                                </p>
                                {dsLabel && (
                                  <span title={dsTitle} data-testid={`email-date-status-badge-${em.id}`} className={`ops-pill ${dsTone}`}>
                                    {dsLabel}
                                  </span>
                                )}
                                {em.toggle_b && (
                                  <span
                                    title="Consent-pending paragraph was included in this send"
                                    data-testid={`email-consent-badge-${em.id}`}
                                    className={`ops-pill ${TONE.info}`}
                                  >
                                    Consent pending
                                  </span>
                                )}
                              </div>
                            </div>
                            {expandedEmail === em.id
                              ? <ChevronUp size={13} className="text-slate-400 shrink-0" />
                              : <ChevronDown size={13} className="text-slate-400 shrink-0" />}
                          </button>
                          {expandedEmail === em.id && (
                            <div className="border-t border-slate-200 bg-slate-50/60 p-4">
                              <div className="text-[11px] text-slate-500 mb-3">From: {em.sender} · Reply-to: {em.reply_to}</div>
                              <div className="text-[13px] text-slate-800 leading-relaxed whitespace-pre-line">{em.body}</div>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            )}

            {/* Activity */}
            {activeTab === 'activity' && (
              <div className="pt-4">
                <div className="flex gap-2 mb-3">
                  <input
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addNote()}
                    placeholder="Call note, update, or observation…"
                    data-testid="note-input"
                    className="flex-1 h-8 bg-white border border-slate-200 focus:border-slate-900 focus:ring-1 focus:ring-slate-900 rounded-md px-2.5 text-[13px] text-slate-900 outline-none placeholder:text-slate-400 transition-colors"
                  />
                  <button
                    onClick={addNote}
                    disabled={sending || !noteText.trim()}
                    data-testid="add-note-button"
                    className="h-8 text-[12px] bg-slate-900 hover:bg-slate-800 text-white px-3 rounded-md font-medium shadow-sm disabled:opacity-50 transition-colors"
                  >Add</button>
                </div>
                {notes.length === 0 ? (
                  <p className="text-[13px] text-slate-500 italic text-center py-4">No activity yet.</p>
                ) : (
                  <ul className="space-y-2 max-h-72 overflow-y-auto">
                    {notes.map(n => (
                      <li key={n.id} data-testid={`note-${n.id}`} className={`rounded-md border-l-2 pl-3 pr-2 py-1.5 ${n.is_system ? 'border-slate-300 bg-slate-50/50' : 'border-slate-900 bg-white'}`}>
                        <p className={`text-[13px] ${n.is_system ? 'text-slate-600 italic' : 'text-slate-900'}`}>{n.text}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-1">
                          <Clock size={10} /> {n.author_name} · {new Date(n.created_at).toLocaleString()}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        </div>
      </OpsModal>

      {showPicker && (
        <SendEmailPicker
          onClose={() => setShowPicker(false)}
          onPick={(tpl) => { setPickedTemplate(tpl); setShowPicker(false); setShowCompose(true); }}
        />
      )}
      {showCompose && (
        <EmailComposeModal
          leadId={leadId}
          template={pickedTemplate}
          onClose={() => { setShowCompose(false); setPickedTemplate(null); }}
          onSent={() => { fetchLead(); onUpdated(); }}
        />
      )}
    </>
  );
}
